import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';

type Num = number | null;

interface TargetModel {
  Start_mass_min: Num;
  Start_mass_max: Num;
  Mass_pn_min: Num;
  Mass_pn_max: Num;
  Volume_pn_min: Num;
  Volume_pn_max: Num;
  Height_output_min: Num;
  Height_output_max: Num;
  Stages_number: Num;
  Start_overload_min: Num;
  Start_overload_max: Num;
  Finish_overload_min: Num;
  Finish_overload_max: Num;
  Tightness: boolean;
  Volume_rocket_min: Num;
  Volume_rocket_max: Num;
  Length_rocket_min: Num;
  Length_rocket_max: Num;
  Diameter_rocket_min: Num;
  Diameter_rocket_max: Num;
}

@Component({
  selector: 'app-special-calc',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './special-calc.component.html',
  styleUrls: ['./special-calc.component.css'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ height: '0px', opacity: 0 })),
      ]),
    ]),
  ],
})
export class SpecialCalcComponent implements OnInit {
  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    console.log('>> SpecialCalcComponent initialized');
    console.log(
      '   route.snapshot.url =',
      this.route.snapshot.url.map((p) => p.path).join('/')
    );
    console.log(
      '   matrix params =',
      Array.from(this.route.snapshot.paramMap.keys)
        .map((k) => `${k}=${this.route.snapshot.paramMap.get(k)}`)
        .join(', ')
    );
  }

  // дефолтные числовые значения (без Tightness)
  readonly def: Omit<TargetModel, 'Tightness'> = {
    Start_mass_min: 500,
    Start_mass_max: 2400000,
    Mass_pn_min: 100,
    Mass_pn_max: 98000,
    Volume_pn_min: 0,
    Volume_pn_max: 0,
    Height_output_min: 0,
    Height_output_max: 0,
    Stages_number: 1,
    Start_overload_min: 0,
    Start_overload_max: 0,
    Finish_overload_min: 0,
    Finish_overload_max: 0,
    Volume_rocket_min: 0,
    Volume_rocket_max: 0,
    Length_rocket_min: 0,
    Length_rocket_max: 0,
    Diameter_rocket_min: 0,
    Diameter_rocket_max: 0,
  };

  // полная модель, включая Tightness
  model: TargetModel = { ...this.def, Tightness: false };

  // UI-флаги
  showExtraFields = false;
  showRequirements = false;
  stagesMode: 'single' | 'multi' = 'single';

  // результаты расчётов
  computed: Record<string, number> = {};
  computedStages: Array<Record<string, number>> = [];

  // для *ngFor="let _ of stagesArray"
  get stagesArray(): any[] {
    const n = this.model.Stages_number! > 0 ? this.model.Stages_number! : 0;
    return Array(n).fill(null);
  }

  // описания полей
  engineFields = [
    { key: 'engineThrust', label: 'Тяга двигателя, Н' },
    { key: 'massEngine', label: 'Масса, кг' },
    { key: 'specificImpulse', label: 'Удельный импульс, м/с' },
    { key: 'fuelCons', label: 'Секундный расход топлива, кг/с' },
  ];

  bodyParts = [
    {
      title: 'Головной обтекатель',
      fields: [
        { label: 'Объём, м³', minKey: 'volumeHeadMin', maxKey: 'volumeHeadMax' },
        { label: 'Масса, кг', minKey: 'massHeadMin', maxKey: 'massHeadMax' },
        { label: 'Длина, м', minKey: 'lengthHeadMin', maxKey: 'lengthHeadMax' },
        { label: 'Диаметр, м', minKey: 'diameterHeadMin', maxKey: 'diameterHeadMax' },
      ],
    },
    {
      title: 'Приборный отсек',
      fields: [
        { label: 'Объём, м³', minKey: 'volumeInstMin', maxKey: 'volumeInstMax' },
        { label: 'Масса, кг', minKey: 'massInstMin', maxKey: 'massInstMax' },
        { label: 'Длина, м', minKey: 'lengthInstMin', maxKey: 'lengthInstMax' },
        { label: 'Диаметр, м', minKey: 'diameterInstMin', maxKey: 'diameterInstMax' },
      ],
    },
    {
      title: 'Переходный отсек',
      fields: [
        { label: 'Объём, м³', minKey: 'volumeInterMin', maxKey: 'volumeInterMax' },
        { label: 'Масса, кг', minKey: 'massInterMin', maxKey: 'massInterMax' },
        { label: 'Длина, м', minKey: 'lengthInterMin', maxKey: 'lengthInterMax' },
        { label: 'Диаметр, м', minKey: 'diameterInterMin', maxKey: 'diameterInterMax' },
      ],
    },
    {
      title: 'Хвостовой отсек',
      fields: [
        { label: 'Объём, м³', minKey: 'volumeTailMin', maxKey: 'volumeTailMax' },
        { label: 'Масса, кг', minKey: 'massTailMin', maxKey: 'massTailMax' },
        { label: 'Длина, м', minKey: 'lengthTailMin', maxKey: 'lengthTailMax' },
        { label: 'Диаметр, м', minKey: 'diameterTailMin', maxKey: 'diameterTailMax' },
      ],
    },
  ];

  fuelTankFields = [
    { label: 'Объём, м³', minKey: 'volumeTankMin', maxKey: 'volumeTankMax' },
    { label: 'Масса, кг', minKey: 'massTankMin', maxKey: 'massTankMax' },
    { label: 'Длина, м', minKey: 'lengthTankMin', maxKey: 'lengthTankMax' },
    { label: 'Диаметр, м', minKey: 'diameterTankMin', maxKey: 'diameterTankMax' },
  ];

  propellantFields = [
    { label: 'Удельный импульс, м/с', minKey: 'propImpulseMin', maxKey: 'propImpulseMax' },
    { label: 'Масса горючего, кг', minKey: 'propMassMin', maxKey: 'propMassMax' },
    { label: 'Плотность, кг/м³', minKey: 'propDensityMin', maxKey: 'propDensityMax' },
  ];

  oxidizerFields = [
    { label: 'Плотность, кг/м³', minKey: 'oxDensityMin', maxKey: 'oxDensityMax' },
    { label: 'Масса окислителя, кг', minKey: 'oxMassMin', maxKey: 'oxMassMax' },
  ];

  onStagesNumberChange(value: number): void {
    this.model.Stages_number = value;             // сохраняем в модель
    this.stagesMode = (value ?? 0) > 1 ? 'multi' : 'single';
  }
  fillIfEmpty(prop: keyof typeof this.def, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    if (!input.value) {
      const v = this.def[prop];
      if (v === null) {
        return;       
      }
      input.value = v.toString();
      (this.model as any)[prop] = v;
    }
  }
  toggleExtra(): void {
    this.showExtraFields = !this.showExtraFields;
  }

  onCancel(): void {
    this.router.navigate(['home/calculation_list']);
  }

  onSubmit(): void {
    this.computeRequirements();
    this.showRequirements = true;
  }

  onRecalculate(): void {
    this.computeRequirements();
  }

  onBack(): void {
    this.showRequirements = false;
  }

  onSave(): void {
    alert('Проект сохранён');
  }

  onSaveAs(): void {
    alert('Сохранить как…');
  }

  onGenerateReport(): void {
    alert('Отчёт сформирован');
  }

  computeRequirements(): void {
    const g = 9.81;

    // Обновляем режим один/многоступенчатый
    this.stagesMode = (this.model.Stages_number ?? 0) > 1 ? 'multi' : 'single';

    // Сбрасываем предыдущие результаты
    this.computed = {};
    this.computedStages = [];

    // Вспомогательная функция: вычисляет полный набор полей для одной ступени
    const calcStage = (massMin: number, massMax: number) => {
      // 1. Двигательная установка
      const thrustMin = (this.model.Start_overload_min! * massMin) * g;
      const thrustMax = (this.model.Finish_overload_max! * massMax) * g;
      const ispMin = 2300;
      const ispMax = 4500;
      const fuelConsMin = thrustMin / ispMin;
      const fuelConsMax = thrustMax / ispMax;
      const massEngineMin = thrustMin / ispMin;
      const massEngineMax = thrustMax / ispMax;

      // 2. Корпусные детали (по объёму полезной нагрузки)
      const vMin = this.model.Volume_pn_min! || 1;
      const vMax = this.model.Volume_pn_max! || 1;

      const volumeHeadMin   = 1.2 * vMin;
      const volumeHeadMax   = 1.2 * vMax;
      const massHeadMin     = volumeHeadMin * 250;
      const massHeadMax     = volumeHeadMax * 250;
      const lengthHeadMin   = Math.cbrt(volumeHeadMin);
      const lengthHeadMax   = Math.cbrt(volumeHeadMax);
      const diameterHeadMin = lengthHeadMin / 2;
      const diameterHeadMax = lengthHeadMax / 2;

      const volumeInstMin   = 1.0 * vMin;
      const volumeInstMax   = 1.0 * vMax;
      const massInstMin     = volumeInstMin * 270;
      const massInstMax     = volumeInstMax * 270;
      const lengthInstMin   = Math.cbrt(volumeInstMin);
      const lengthInstMax   = Math.cbrt(volumeInstMax);
      const diameterInstMin = lengthInstMin / 2;
      const diameterInstMax = lengthInstMax / 2;

      const volumeInterMin   = 0.8 * vMin;
      const volumeInterMax   = 0.8 * vMax;
      const massInterMin     = volumeInterMin * 260;
      const massInterMax     = volumeInterMax * 260;
      const lengthInterMin   = Math.cbrt(volumeInterMin);
      const lengthInterMax   = Math.cbrt(volumeInterMax);
      const diameterInterMin = lengthInterMin / 2;
      const diameterInterMax = lengthInterMax / 2;

      const volumeTailMin   = 0.9 * vMin;
      const volumeTailMax   = 0.9 * vMax;
      const massTailMin     = volumeTailMin * 240;
      const massTailMax     = volumeTailMax * 240;
      const lengthTailMin   = Math.cbrt(volumeTailMin);
      const lengthTailMax   = Math.cbrt(volumeTailMax);
      const diameterTailMin = lengthTailMin / 2;
      const diameterTailMax = lengthTailMax / 2;

      // 3. Топливный отсек (по объёму ракеты)
      const rvMin = this.model.Volume_rocket_min! || 1;
      const rvMax = this.model.Volume_rocket_max! || 1;
      const volumeTankMin   = 0.7 * rvMin;
      const volumeTankMax   = 0.7 * rvMax;
      const massTankMin     = volumeTankMin * 240;
      const massTankMax     = volumeTankMax * 240;
      const lengthTankMin   = Math.cbrt(volumeTankMin);
      const lengthTankMax   = Math.cbrt(volumeTankMax);
      const diameterTankMin = lengthTankMin / 2;
      const diameterTankMax = lengthTankMax / 2;

      // 4. Горючее и окислитель (по массе полезной нагрузки)
      const propImpulseMin  = ispMin;
      const propImpulseMax  = ispMax;
      const propMassMin     = this.model.Mass_pn_min! * 0.5;
      const propMassMax     = this.model.Mass_pn_max! * 0.5;
      const propDensityMin  = 800;
      const propDensityMax  = 900;

      const oxDensityMin    = 1100;
      const oxDensityMax    = 1200;
      const oxMassMin       = this.model.Mass_pn_min! * 0.5;
      const oxMassMax       = this.model.Mass_pn_max! * 0.5;

      return {
        engineThrustMin: thrustMin,
        engineThrustMax: thrustMax,
        specificImpulseMin: ispMin,
        specificImpulseMax: ispMax,
        fuelConsMin,
        fuelConsMax,
        massEngineMin,
        massEngineMax,

        volumeHeadMin,
        volumeHeadMax,
        massHeadMin,
        massHeadMax,
        lengthHeadMin,
        lengthHeadMax,
        diameterHeadMin,
        diameterHeadMax,

        volumeInstMin,
        volumeInstMax,
        massInstMin,
        massInstMax,
        lengthInstMin,
        lengthInstMax,
        diameterInstMin,
        diameterInstMax,

        volumeInterMin,
        volumeInterMax,
        massInterMin,
        massInterMax,
        lengthInterMin,
        lengthInterMax,
        diameterInterMin,
        diameterInterMax,

        volumeTailMin,
        volumeTailMax,
        massTailMin,
        massTailMax,
        lengthTailMin,
        lengthTailMax,
        diameterTailMin,
        diameterTailMax,

        volumeTankMin,
        volumeTankMax,
        massTankMin,
        massTankMax,
        lengthTankMin,
        lengthTankMax,
        diameterTankMin,
        diameterTankMax,

        propImpulseMin,
        propImpulseMax,
        propMassMin,
        propMassMax,
        propDensityMin,
        propDensityMax,

        oxDensityMin,
        oxDensityMax,
        oxMassMin,
        oxMassMax,
      };
    };

    // 1-я ступень: исходные остаточные массы
    let remMin = this.model.Start_mass_min! || 0;
    let remMax = this.model.Start_mass_max! || 0;

    // Если одноступенчатый режим, просто кладём результат первой ступени в computed
    if (this.stagesMode === 'single') {
      this.computed = calcStage(remMin, remMax);
      return;
    }

    // Многоступенчатый — последовательно рассчитываем каждую
    for (let i = 0; i < (this.model.Stages_number ?? 0); i++) {
      const stage = calcStage(remMin, remMax);
      this.computedStages.push(stage);

      // Вычитаем из остатка всё, что набралось в этой ступени
      remMin -= (
        stage.massEngineMin +
        stage.massHeadMin +
        stage.massInstMin +
        stage.massTailMin +
        stage.massTankMin +
        stage.propMassMin +
        stage.oxMassMin
      );
      remMax -= (
        stage.massEngineMax +
        stage.massHeadMax +
        stage.massInstMax +
        stage.massTailMax +
        stage.massTankMax +
        stage.propMassMax +
        stage.oxMassMax
      );
    }
  }

}